import { useEffect } from 'react';

import { Button, Flex, ThemeProvider, Typography } from '@aircall/tractor-v2';
import aircall_text_logo from '@assets/icons/aircall_text_logo.svg';
import kyc_verification_logo from '@assets/images/kyc_verification_logo.svg';
import { useHandleButtonLink } from '@components/KycBanner';
import { ONBOARDING_ROUTE } from '@constants/routes.constants';
import { Loading } from '@dashboard/library';
import { useFeaturesFlags } from '@hooks/useFeatures/useFeaturesFlags';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

function KycVerificationPage() {
  const { dashboardEnableStripeKyc } = useFeaturesFlags();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { handleButtonLink, loading } = useHandleButtonLink();

  const handleInitiateLater = () => {
    navigate(ONBOARDING_ROUTE, { replace: true });
  };

  useEffect(() => {
    if (!dashboardEnableStripeKyc) {
      navigate(ONBOARDING_ROUTE, { replace: true });
    }
  }, [navigate, dashboardEnableStripeKyc]);

  if (!dashboardEnableStripeKyc) {
    return <Loading data-test='generic-page-loader' />;
  }
  return (
    <ThemeProvider>
      <Flex
        data-test='kyc-verification-page-wrapper'
        position='relative'
        flexDirection='column'
        justifyContent='flex-start'
        padding='l'
        paddingTop='xxl'
        background='radial-gradient(126.12% 126.12% at 50.91% 173.88%, #97f1d5 0%, #fff 100%)'
        gap='xxl'
        overflowY='auto'
        w='100%'
        h='100%'
      >
        <Flex alignItems='center' alignSelf='flex-start' justifyContent='center'>
          <img src={aircall_text_logo} alt='aircall_text_logo' />
        </Flex>
        <Flex
          data-test='content-wrapper'
          position='relative'
          maxWidth='479px'
          maxHeight='510px'
          flexDirection='column'
          margin='0 auto'
          marginTop='xl'
        >
          <Flex flexDirection='column' alignItems='center' gap='s' paddingBottom='s'>
            <img src={kyc_verification_logo} alt='keys_and_message' />
            <Typography variant='headingBoldS'>
              {t('stripe.kyc.verification_page.title')}
            </Typography>
          </Flex>
          <Flex flexDirection='column' alignItems='center' gap='m'>
            <Typography variant='bodyRegularM' textAlign='center'>
              {t('stripe.kyc.verification_page.description')}
            </Typography>

            <Typography variant='bodyRegularM' textAlign='center'>
              {t('stripe.kyc.verification_page.consent_text')}
            </Typography>

            <Flex flexDirection='row' alignItems='center' marginTop='s' gap='s'>
              <Button
                variant='primary'
                size='large'
                mode='outline'
                shape='circle'
                data-test='not-now-button'
                onClick={handleInitiateLater}
                min-width='120px'
              >
                {t('stripe.kyc.verification_page.initiate_later')}
              </Button>
              <Button
                variant='primary'
                size='large'
                mode='fill'
                shape='circle'
                data-test='verify-now-button'
                onClick={() => handleButtonLink(true)}
                min-width='120px'
              >
                {t('stripe.kyc.verification_page.initiate_kyc')}
              </Button>
            </Flex>
            <Flex flexDirection='column' alignItems='center' gap='m'>
              <Button
                variant='secondary'
                mode='link'
                data-test='verify-now-button-as-link'
                onClick={() => handleButtonLink(false)}
                style={{
                  whiteSpace: 'normal',
                  textAlign: 'center',
                  width: '100%',
                  minWidth: '120px',
                }}
                className='warning-link'
              >
                <Typography variant='bodyRegularM' textAlign='center'>
                  {t('stripe.kyc.verification_page.consent_alternative_link')}
                </Typography>
              </Button>
            </Flex>
            <Flex flexDirection='column' alignItems='center' gap='m'>
              <Loading
                display={loading ? 'block' : 'none'}
                flexDirection='column'
                alignItems='center'
              />
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </ThemeProvider>
  );
}

export default KycVerificationPage;
